// "That day is wrong" — the panel behind the Appeal Request button.
//
// Unlike the early-exit dialog (which fires at the moment of the punch and
// knows exactly which day it means) this one has to ask for the date first.
// Everything after that is shaped by the type chosen: only a timing appeal
// needs times, so only a timing appeal is asked for them; only work-from-home
// and overtime can name a span of days (and days ahead — approving those is
// how the employee gets the right rule BEFORE they clock in), so only they get
// a "To" date. A timing appeal alone is held to a day that has already
// happened: a punch that hasn't landed can't be wrong yet. A form that showed
// all four types' fields at once would make three quarters of it noise on
// every single use.
//
// The reason is mandatory for the same reason it is on early exit: an approver
// reading "appealing 3 Sep" with nothing beside it cannot decide, and a request
// nobody can decide is worse than no appeal route at all.

import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  raiseAttendanceAppeal,
  type AttendanceAppealResult,
  type AttendanceAppealType,
} from '../../api/attendance';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected day (YYYY-MM-DD) when opened from a specific row. */
  defaultDate?: string | null;
  /** The punch being appealed, when opened from one. */
  defaultRecordId?: string | null;
  onResult: (result: AttendanceAppealResult) => void;
}

// Mirrors utils/attendance_appeal.APPEAL_TYPES. Held here as well as fetched
// from the list endpoint so the picker renders instantly — the copy is short,
// and a dialog that had to wait on a round trip to show four radio buttons
// would feel broken.
const TYPES: {
  key: AttendanceAppealType;
  label: string;
  blurb: string;
  needsTimes: boolean;
  /** Can name a span of days, and days ahead. */
  takesRange: boolean;
  /** Only a day that has already happened. */
  pastOnly: boolean;
}[] = [
  {
    key: 'timing',
    label: 'Timing issue',
    blurb: 'The clock-in or clock-out time on the record is wrong.',
    needsTimes: true,
    takesRange: false,
    pastOnly: true,
  },
  {
    key: 'work_from_home',
    label: 'Work from home',
    blurb: 'You worked or will work from home and the days should be recorded as such.',
    needsTimes: false,
    takesRange: true,
    pastOnly: false,
  },
  {
    key: 'overtime',
    label: 'Overtime allowed',
    blurb: 'Hours beyond your shift on these days should count as overtime.',
    needsTimes: false,
    takesRange: true,
    pastOnly: false,
  },
  {
    key: 'other',
    label: 'Something else',
    blurb: 'Anything the three above do not cover.',
    needsTimes: false,
    takesRange: false,
    pastOnly: false,
  },
];

// Mirrors AttendanceAppeal.MAX_RANGE_DAYS: longer than a month is the default
// work mode's job, not an exception to it.
const MAX_RANGE_DAYS = 31;

const MIN_REASON = 3;
const todayStr = () => {
  // Local calendar date, not toISOString(): in IST the UTC date is yesterday
  // for the first 5½ hours of every day, which would cap the picker a day short.
  const d = new Date();
  const p = (v: number) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function AppealRequestDialog({
  open,
  onOpenChange,
  defaultDate,
  defaultRecordId,
  onResult,
}: Props) {
  const [onDate, setOnDate] = useState(defaultDate || todayStr());
  const [toDate, setToDate] = useState('');
  const [appealType, setAppealType] = useState<AttendanceAppealType>('timing');
  const [reason, setReason] = useState('');
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh ask each time it opens: a half-typed appeal about last Tuesday
  // reappearing over this Friday's is how the wrong day gets sent.
  useEffect(() => {
    if (!open) return;
    setOnDate(defaultDate || todayStr());
    setToDate('');
    setAppealType('timing');
    setReason('');
    setClockIn('');
    setClockOut('');
    setError(null);
  }, [open, defaultDate]);

  const selected = useMemo(
    () => TYPES.find((t) => t.key === appealType) || TYPES[0],
    [appealType],
  );

  const needsTimes = selected.needsTimes;
  const takesRange = selected.takesRange;
  const timesMissing = needsTimes && !clockIn && !clockOut;
  // The "already happened" rule is timing's alone; the other types may look
  // ahead. The date input's `max` enforces it for the picker, this catches a
  // day typed in or carried over from a type that allowed it.
  const dayAhead = selected.pastOnly && !!onDate && onDate > todayStr();
  const spanDays =
    takesRange && toDate && onDate
      ? Math.round(
          (new Date(`${toDate}T00:00:00`).getTime() -
            new Date(`${onDate}T00:00:00`).getTime()) /
            86_400_000,
        ) + 1
      : 1;
  const spanBackwards = spanDays < 1;
  const spanTooLong = spanDays > MAX_RANGE_DAYS;
  const canSend =
    !busy &&
    !!onDate &&
    reason.trim().length >= MIN_REASON &&
    !timesMissing &&
    !dayAhead &&
    !spanBackwards &&
    !spanTooLong;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await raiseAttendanceAppeal({
        onDate,
        // A "To" only travels with the types that mean a span — a stale one
        // left over from a switched-away type must not widen a timing fix.
        toDate: takesRange && toDate && toDate !== onDate ? toDate : null,
        appealType,
        reason: reason.trim(),
        // Times only travel with the type that means them — sending a stale
        // 18:30 along with a work-from-home appeal would store an ask nobody
        // made.
        requestedClockIn: needsTimes ? clockIn || null : null,
        requestedClockOut: needsTimes ? clockOut || null : null,
        recordId: defaultRecordId || null,
      });
      onResult(result);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message || 'Could not send the appeal. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] grid-rows-[auto_minmax(0,1fr)_auto] gap-3 p-5">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            Appeal an attendance day
          </DialogTitle>
          <DialogDescription>
            Goes to your reporting manager and the admins. Whoever acts first
            decides, and an approval adjusts the day for you.
          </DialogDescription>
        </DialogHeader>

        {/* The only scroller: on a short window the type cards and the reason
            slide under the header rather than pushing the buttons off-screen. */}
        <div className="space-y-3 overflow-y-auto -mr-1 pr-1">
          <div className="space-y-1.5">
            <label
              htmlFor="appeal-date"
              className="text-sm font-medium text-foreground"
            >
              {takesRange ? 'Which days?' : 'Which day?'}
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="appeal-date"
                type="date"
                value={onDate}
                aria-label={takesRange ? 'From' : 'Which day?'}
                // Only a timing appeal is capped at today; the others may
                // name days still to come.
                max={selected.pastOnly ? todayStr() : undefined}
                onChange={(e) => setOnDate(e.target.value)}
                className="w-full rounded-md border border-input bg-background p-2 text-sm text-foreground outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 sm:w-44"
              />
              {takesRange && (
                <>
                  <span className="text-xs text-muted-foreground">to</span>
                  <input
                    id="appeal-to-date"
                    type="date"
                    value={toDate}
                    aria-label="To (optional)"
                    min={onDate || undefined}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full rounded-md border border-input bg-background p-2 text-sm text-foreground outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 sm:w-44"
                  />
                </>
              )}
            </div>
            {takesRange && !spanBackwards && !spanTooLong && (
              <p className="text-xs text-muted-foreground">
                Leave the second date empty for a single day. Days still to
                come are allowed — once approved they are set up before you
                clock in.
              </p>
            )}
            {dayAhead && (
              <p className="text-xs text-red-600" role="alert">
                Timing can only be corrected for a day that has already happened.
              </p>
            )}
            {spanBackwards && (
              <p className="text-xs text-red-600" role="alert">
                The last day can&apos;t be before the first.
              </p>
            )}
            {spanTooLong && (
              <p className="text-xs text-red-600" role="alert">
                Appeal at most {MAX_RANGE_DAYS} days at a time — for longer, ask
                HR to change your work mode.
              </p>
            )}
          </div>

          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium text-foreground">
              What went wrong?
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {TYPES.map((t) => {
                const active = t.key === appealType;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setAppealType(t.key)}
                    aria-pressed={active}
                    className={`rounded-lg border p-2 text-left transition-colors ${
                      active
                        ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:ring-emerald-800'
                        : 'border-input hover:bg-accent'
                    }`}
                  >
                    <span
                      className={`block text-sm font-semibold ${
                        active
                          ? 'text-emerald-800 dark:text-emerald-300'
                          : 'text-foreground'
                      }`}
                    >
                      {t.label}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                      {t.blurb}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {needsTimes && (
            // Both sides are optional individually — plenty of appeals are
            // "my clock-out is wrong, the clock-in is fine" — but at least one
            // has to be given or there is nothing to correct.
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label
                  htmlFor="appeal-in"
                  className="text-sm font-medium text-foreground"
                >
                  Correct clock-in
                </label>
                <input
                  id="appeal-in"
                  type="time"
                  value={clockIn}
                  onChange={(e) => setClockIn(e.target.value)}
                  className="w-full rounded-md border border-input bg-background p-2 text-sm text-foreground outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="appeal-out"
                  className="text-sm font-medium text-foreground"
                >
                  Correct clock-out
                </label>
                <input
                  id="appeal-out"
                  type="time"
                  value={clockOut}
                  onChange={(e) => setClockOut(e.target.value)}
                  className="w-full rounded-md border border-input bg-background p-2 text-sm text-foreground outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <p className="sm:col-span-2 -mt-1 text-xs text-muted-foreground">
                Fill in whichever side is wrong — leave the other blank and it
                stays as it is.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <label
              htmlFor="appeal-reason"
              className="text-sm font-medium text-foreground"
            >
              Why?
            </label>
            <textarea
              id="appeal-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="Stayed back to finish the release, clocked out on my phone but it didn't save"
              className="w-full rounded-md border border-input bg-background p-2 text-sm text-foreground outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
            <p className="text-xs text-muted-foreground">
              Your approver sees this next to what the record currently says.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {error && (
            <p className="mr-auto text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSend}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Send className="mr-1.5 h-3.5 w-3.5" />
            {busy ? 'Sending…' : 'Send appeal'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
